<template>
  <!-- Échap fermerait la modale expirée et laisserait le fond bloquant sans moyen de se reconnecter -->
  <div
    class="session-expiree-overlay"
    @keydown.esc.capture.stop.prevent="onEchap()"
    @cancel.capture.prevent="onEchap()"
  >
    <Modal
      id="modal_session_expiree"
      variant="warning"
    >
      <template #title>
        {{ titre }}
      </template>
      <template #content>
        <template v-if="enAvertissement">
          <p class="text-regular message">
            {{ $t("session_expiree.avertissement_message") }}
          </p>
        </template>
        <template v-else>
          <p class="text-regular message">
            {{ $t("session_expiree.message") }}
          </p>
          <p class="text-regular message avertissement">
            {{ $t("session_expiree.avertissement") }}
          </p>
          <p
            v-if="connexionEnCours"
            class="text-small message en-attente"
          >
            {{ $t("session_expiree.en_attente") }}
          </p>
        </template>
      </template>
      <template #footer>
        <template v-if="enAvertissement">
          <ButtonClassic
            variant="ghost"
            color="primary"
            :label="$t('session_expiree.plus_tard')"
            @click="plusTard()"
          />
          <ButtonClassic
            variant="solid"
            color="primary"
            :label="$t('session_expiree.rester_connecte')"
            :disabled="prolongationEnCours"
            @click="resterConnecte()"
          />
        </template>
        <ButtonClassic
          v-else
          variant="solid"
          color="primary"
          :label="$t('session_expiree.se_reconnecter')"
          :disabled="connexionEnCours"
          @click="ouvrirConnexion()"
        />
      </template>
    </Modal>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";

import { ButtonClassic, Modal } from "@lde/core_lde_vue";

import config from "@/config";
import { abandonnerRequetesEnAttente, rejouerRequetesEnAttente } from "@/modules/finSession";
import {
  cleVerrouReconnexion,
  lireVerrouReconnexion,
  poserVerrouReconnexion,
} from "@/modules/verrouReconnexion";
import { useToast } from "@/utils/toast";

// Décompte affiché en tête du titre de l'onglet, pour qu'il se voie même depuis un autre onglet
const prefixeTitre = /^\(\d+:\d{2}\) /;

/**
 * Modale de fin de session, en deux états : avant l'échéance, un avertissement avec décompte qui propose
 * de prolonger la session ; à l'échéance, elle devient la modale bloquante de session expirée.
 * Elle se superpose à la page sans la démonter, pour que rien de ce qui est en cours ne soit perdu.
 * La reconnexion se fait dans un nouvel onglet ; la page d'arrivée (/session-retablie) prévient cet onglet
 * via localStorage, et le retour sur l'onglet sert de filet si l'événement n'arrive pas.
 */
export default {
  name: "ModalSessionExpiree",
  components: {
    Modal,
    ButtonClassic,
  },
  data() {
    return {
      // Fin du verrou de reconnexion partagé entre onglets (horodatage, 0 = aucune reconnexion en cours)
      verrouJusqua: 0,
      verificationEnCours: false,
      prolongationEnCours: false,
      horloge: Date.now(),
    };
  },
  computed: {
    ...mapState(["echeanceSession"]),
    ...mapGetters(["sessionExpiree"]),
    enAvertissement() {
      return !this.sessionExpiree;
    },
    connexionEnCours() {
      return this.horloge < this.verrouJusqua;
    },
    titre() {
      return this.enAvertissement
        ? this.$t("session_expiree.avertissement_titre", { temps: this.tempsRestant })
        : this.$t("session_expiree.titre");
    },
    /**
     * Temps restant au format m:ss, à l'heure du serveur.
     * @returns {String}
     */
    tempsRestant() {
      const echeance = this.echeanceSession;
      const restantMs = echeance ? echeance.expireLe - (this.horloge + echeance.decalage) : 0;
      const secondes = Math.max(0, Math.ceil(restantMs / 1000));
      return `${Math.floor(secondes / 60)}:${String(secondes % 60).padStart(2, "0")}`;
    },
  },
  watch: {
    tempsRestant: {
      immediate: true,
      handler() {
        this.majTitreOnglet();
      },
    },
    enAvertissement() {
      this.majTitreOnglet();
    },
  },
  mounted() {
    this.minuteur = setInterval(() => {
      this.horloge = Date.now();
    }, 1000);
    this.verrouJusqua = lireVerrouReconnexion();

    this.$nextTick(() => {
      if (this.$modal) {
        this.$modal.show("modal_session_expiree");
      }
      // Focus hors de la modale, le navigateur peut la fermer sur Échap malgré « cancel » (Chrome l'autorise
      // à la deuxième pression) : on rattrape la fermeture elle-même
      this.dialogue = document.getElementById("modal_session_expiree");
      this.dialogue?.addEventListener("close", this.onDialogueFerme);
    });

    this.onStorageChange = (e) => {
      if (e.key === "session_event") {
        this.verifierRetour();
      } else if (e.key === cleVerrouReconnexion) {
        this.verrouJusqua = lireVerrouReconnexion();
      }
    };
    window.addEventListener("storage", this.onStorageChange);
    window.addEventListener("focus", this.verifierRetour);
  },
  beforeUnmount() {
    this.dialogue?.removeEventListener("close", this.onDialogueFerme);
    clearInterval(this.minuteur);
    document.title = document.title.replace(prefixeTitre, "");
    window.removeEventListener("storage", this.onStorageChange);
    window.removeEventListener("focus", this.verifierRetour);
  },
  methods: {
    /**
     * Le routeur réécrit le titre à chaque navigation : on repart du titre sans décompte à chaque seconde.
     */
    majTitreOnglet() {
      const titre = document.title.replace(prefixeTitre, "");
      document.title = this.enAvertissement ? `(${this.tempsRestant}) ${titre}` : titre;
    },
    /**
     * La fenêtre a été fermée par le navigateur : en avertissement, cela vaut « Plus tard » ;
     * expirée, elle est rouverte, sinon il ne resterait qu'un fond noir sans bouton.
     */
    onDialogueFerme() {
      if (this.enAvertissement) {
        this.plusTard();
      } else if (!this.dialogue.open) {
        this.dialogue.showModal();
      }
    },
    /**
     * Échap ne ferme que l'avertissement ; la modale expirée ne se quitte qu'en se reconnectant.
     */
    onEchap() {
      if (this.enAvertissement) {
        this.plusTard();
      }
    },
    /**
     * Repousse l'avertissement ; à l'échéance, la modale revient en « session expirée ».
     */
    plusTard() {
      this.$store.commit("ignorerAvertissementSession");
    },
    /**
     * Prolonge la session sur clic explicite ; la nouvelle échéance referme l'avertissement.
     */
    resterConnecte() {
      this.prolongationEnCours = true;
      this.$store.dispatch("prolongerSession")
        .catch((err) => {
          // Une 401 fait déjà passer la modale en « session expirée »
          if (err?.response?.status !== 401) {
            useToast().error({ content: this.$t("session_expiree.prolongation_echec") });
          }
        })
        .finally(() => {
          this.prolongationEnCours = false;
        });
    },
    /**
     * Ouvre la connexion dans un nouvel onglet : rediriger celui-ci détruirait le travail en cours.
     * Une seule reconnexion à la fois, tous onglets confondus (modules/verrouReconnexion). Le lien avec l'onglet
     * ouvert est coupé par les redirections du CAS (fenêtre vue fermée, nom effacé) : on ne peut pas s'y fier.
     */
    ouvrirConnexion() {
      if (this.connexionEnCours) return;
      this.verrouJusqua = poserVerrouReconnexion();
      const retour = encodeURIComponent(`${window.location.origin}/session-retablie`);
      window.open(`${config.loginUrl}?next=${retour}`, "_blank");
    },
    /**
     * Vérifie si l'utilisateur s'est reconnecté ; la modale se ferme d'elle-même si c'est le même compte.
     */
    async verifierRetour() {
      // En avertissement, la session est encore valide : rien à reprendre
      if (this.enAvertissement || this.verificationEnCours) return;
      this.verificationEnCours = true;
      const resultat = await this.$store.dispatch("verifierRetourSession");
      if (resultat === "meme_utilisateur") {
        rejouerRequetesEnAttente();
      } else if (resultat === "autre_utilisateur") {
        // Un autre compte s'est connecté sur ce poste : rien de cette page ne doit lui parvenir
        abandonnerRequetesEnAttente();
        this.$store.commit("setIsRedirecting", true);
        window.location.reload();
        return;
      }
      this.verificationEnCours = false;
    },
  },
};
</script>

<style lang="scss">
@use "~@lde/core_lde_vue/dist/assets/styles/_variables.scss" as var;

.session-expiree-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.6);
}

#modal_session_expiree {
  // Seuls les boutons du footer permettent de sortir de la modale (Échap aussi, en avertissement)
  .modal-header .button {
    display: none;
  }

  .modal-footer {
    gap: var.$space-x-tiny;
  }

  .avertissement {
    font-weight: bold;
  }
}
</style>
